import React, { useEffect, useState } from 'react';
import {
  Stack,
  Typography,
  Box,
  Divider,
  Button,
  Toolbar,
  Tab,
} from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';

import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useApi } from '@/providers/ApiContext';
import { useDocTypes } from '@/providers/DocTypesContext';
import LoadingMask from '@/components/shared/LoadingMask';
import AdminChartList from './AdminChartList';
import { useLocation, useNavigate } from 'react-router';
import ChartDeleteDialog from './ChartDeleteDialog';
import { deleteChart } from '@/actions/chart';
import type { DocumentType, EntityId } from '@/types';
import type { GridRowId } from '@mui/x-data-grid';

function AdminCharts(): React.JSX.Element {
  const doc_types = useDocTypes();
  const navigate = useNavigate();
  const location = useLocation();
  const api = useApi();
  const [document_types, setDocumentTypes] = useState<DocumentType[] | null>(
    null,
  );
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [selected, setSelected] = useState<GridRowId[]>([]);
  const [selectedNames, setSelectedNames] = useState<string[]>([]);
  const [revision, setRevision] = useState<number>(0);

  const handleSelectionChange = (ids: GridRowId[], names?: string[]): void => {
    setSelected(ids);
    setSelectedNames(names ?? []);
  };

  const searchParams = new URLSearchParams(location.search);
  const tab = searchParams.get('type') || 'pending';

  useEffect(() => {
    if (doc_types && !document_types) {
      const filteredArray = doc_types.filter(
        (doc_type) => doc_type.enable_charts,
      );
      setDocumentTypes(filteredArray);
    }
  }, [doc_types, document_types]);

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: string,
  ): void => {
    navigate(`?type=${newValue}`);
  };

  const handleDeleteCharts = async (chart_ids: EntityId[]): Promise<void> => {
    if (!api || !chart_ids.length) return;
    await Promise.all(chart_ids.map((id) => deleteChart(api, id)));
    setRevision((prevState) => prevState + 1);
    setDeleteOpen(false);
  };

  if (!document_types) {
    return <LoadingMask />;
  }

  return (
    <>
      <Toolbar
        sx={{
          minHeight: '55px !important',
        }}
      >
        <Typography variant='h6' sx={{ mr: 4 }}>
          Manage Charts
        </Typography>
        <Stack direction={'row'} spacing={2}>
          <Button
            onClick={() => {
              navigate({
                pathname: '/admin/charts/edit',
                search: tab !== 'pending' ? `?type=${tab}` : '',
              });
            }}
            variant='contained'
            startIcon={<AddCircleOutlineOutlinedIcon />}
          >
            New Chart
          </Button>
          <Button
            onClick={() => navigate(`/admin/charts/${selected[0]}`)}
            disabled={selected.length !== 1}
            startIcon={<EditOutlinedIcon />}
          >
            Edit
          </Button>
          <Button
            onClick={() => setDeleteOpen(true)}
            disabled={selected.length < 1}
            startIcon={<DeleteOutlinedIcon />}
          >
            Delete
          </Button>
        </Stack>
      </Toolbar>
      <Divider />
      <Box sx={{ width: '100%', typography: 'body1' }}>
        <TabContext value={tab}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <TabList onChange={handleTabChange} aria-label='Manage Charts'>
              <Tab label='Pending Review' value='pending' />
              {document_types.map((document_type) => {
                return (
                  <Tab
                    key={document_type.name}
                    label={document_type.name}
                    value={`${document_type.code}`}
                  />
                );
              })}
            </TabList>
          </Box>
          <TabPanel key={`pending-${revision}`} value='pending'>
            <AdminChartList pending_only setSelected={handleSelectionChange} />
          </TabPanel>
          {document_types.map((document_type) => {
            return (
              <TabPanel
                key={`${document_type.code}-${revision}`}
                value={`${document_type.code}`}
              >
                <AdminChartList
                  document_type_id={document_type.entity_id}
                  setSelected={handleSelectionChange}
                />
              </TabPanel>
            );
          })}
        </TabContext>
      </Box>
      <ChartDeleteDialog
        chart_ids={selected as EntityId[]}
        chart_names={selectedNames}
        deleteCharts={handleDeleteCharts}
        open={deleteOpen}
        handleClose={() => setDeleteOpen(false)}
      />
    </>
  );
}

export default AdminCharts;
